import React, { useEffect, useState } from 'react';
import { API } from '../api';
import { formatCount, formatMoney, formatPercent } from '../utils/adminFormatters';
import './SurveyMetricsTab.css';

const DIMENSION_LABELS = {
  ageRange: 'Возраст',
  gender: 'Пол',
  occupation: 'Род занятий',
  discoverySource: 'Источник',
};

const VALUE_LABELS = {
  ageRange: {
    '18_21': '18–21',
    '22_29': '22–29',
    '30_39': '30–39',
    '40_49': '40–49',
    '50_plus': '50+' 
  },
  gender: {
    male: 'Мужчина',
    female: 'Женщина',
    non_binary: 'Небинарный',
  },
  occupation: {
    employed: 'Работаю',
    student: 'Студент',
    freelance: 'Фриланс',
    unemployed: 'Безработный',
  },
  discoverySource: {
    social_media: 'Соцсети',
    search: 'Поиск',
    friends: 'Друзья',
    other: 'Другое',
  },
};

const formatCategoryLabel = (dimension, value) => {
  if (value === null || value === undefined) return '—';
  return VALUE_LABELS[dimension]?.[value] || value;
};

const DistributionPanel = ({ dimension, distribution }) => {
  if (!distribution || !distribution.categories) {
    return null;
  }

  const categories = distribution.categories || [];
  const maxRespondents = Math.max(...categories.map((category) => Number(category.respondents) || 0), 1);

  return (
    <div className="survey-distribution-panel">
      <div className="survey-panel-header">
        <h3>{DIMENSION_LABELS[dimension] || dimension}</h3>
        <span>{formatCount(distribution.totalRespondents)} ответов</span>
      </div>

      {categories.map((category) => (
        <div key={`${dimension}-${category.value}`} className="survey-category-block">
          <div className="survey-category-topline">
            <span>{formatCategoryLabel(dimension, category.value)}</span>
            <span>{formatPercent(category.sharePercent)}</span>
          </div>

          <div className="survey-bar-track">
            <div
              className="survey-bar-fill"
              style={{ width: `${((Number(category.respondents) || 0) / maxRespondents) * 100}%` }}
            />
          </div>

          <div className="survey-category-meta">
            <span>{formatCount(category.respondents)} респондентов</span>
            <span>{formatMoney(category.revenueRub)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const SurveyMetricsTab = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await API.stats.getSurveyMetrics();
      if (result.success) {
        setMetrics(result.data);
      }
      setLoading(false);
    };

    load();
  }, []);

  if (loading) {
    return (
      <div className="tab-content">
        <div className="stats-section loading-state">
          <div className="spinner"></div>
          <p>Загрузка анкетных метрик...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="tab-content">
        <div className="stats-section">
          <div className="empty-message">Не удалось загрузить метрики анкет.</div>
        </div>
      </div>
    );
  }

  const survey = metrics.survey || {};
  const revenue = metrics.revenue || {};
  const profileCategories = revenue.profileBySurvey?.categories || {};

  const cards = [
    {
      label: 'Всего пользователей',
      value: formatCount(survey.totalUsers),
      caption: 'Все зарегистрированные аккаунты',
    },
    {
      label: 'Анкеты заполнены',
      value: formatCount(survey.respondents),
      caption: `${formatPercent(survey.responseRatePercent)} отклика`,
    },
    {
      label: 'Без анкеты',
      value: formatCount(survey.missingResponses),
      caption: 'Пользователи без ответов',
    },
    {
      label: 'Выручка',
      value: formatMoney(revenue.totalRub),
      caption: `${formatCount(revenue.successfulPayments)} успешных платежей`,
    },
  ];

  return (
    <div className="tab-content survey-metrics-tab">
      <div className="stats-section">
        <div className="stats-header">
          <h2>Анкеты и выручка</h2>
          <div className="stats-caption">{new Date(metrics.calculatedAt).toLocaleString('ru-RU')}</div>
        </div>

        <div className="stats-grid survey-overview-grid">
          {cards.map((card) => (
            <div key={card.label} className="stat-card survey-stat-card">
              <div className="stat-header-content">
                <h3>{card.label}</h3>
              </div>
              <div className="stat-value">{card.value}</div>
              <div className="stat-meta">{card.caption}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="stats-section survey-profile-block">
        <h3>Портрет наиболее прибыльных пользователей</h3>
        {revenue.highestRevenueUser ? (
          <div className="survey-top-user-card">
            <div className="survey-text-row">
              <strong>{revenue.highestRevenueUser.firstName} {revenue.highestRevenueUser.lastName}</strong>
              <span>@{revenue.highestRevenueUser.username}</span>
            </div>
            <div className="survey-text-row">
              <span>Выручка:</span>
              <strong>{formatMoney(revenue.highestRevenueUser.revenueRub)}</strong>
            </div>
            <div className="survey-text-row">
              <span>Платежи:</span>
              <strong>{formatCount(revenue.highestRevenueUser.successfulPayments)}</strong>
            </div>
            <div className="survey-text-row">
              <span>Средний чек:</span>
              <strong>{formatMoney(revenue.highestRevenueUser.averageCheckRub)}</strong>
            </div>
            <div className="survey-text-row">
              <span>Профиль:</span>
              <strong>
                {formatCategoryLabel('ageRange', revenue.highestRevenueUser.survey?.ageRange)} / {formatCategoryLabel('gender', revenue.highestRevenueUser.survey?.gender)} / {formatCategoryLabel('occupation', revenue.highestRevenueUser.survey?.occupation)} / {formatCategoryLabel('discoverySource', revenue.highestRevenueUser.survey?.discoverySource)}
              </strong>
            </div>
          </div>
        ) : (
          <div className="empty-message">Пока нет успешных платежей.</div>
        )}
      </div>

      <div className="stats-section">
        <div className="stats-header">
          <h2>Распределение ответов</h2>
          <div className="stats-caption">По каждому вопросу анкеты</div>
        </div>

        <div className="survey-distribution-grid">
          {Object.entries(survey.distributions || {}).map(([dimension, distribution]) => (
            <DistributionPanel key={dimension} dimension={dimension} distribution={distribution} />
          ))}
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-header">
          <h2>Автоматический портрет по выручке</h2>
          <div className="stats-caption">Категория с максимальной выручкой среди заполнивших анкету</div>
        </div>

        <div className="survey-profile-grid">
          {Object.entries(profileCategories).map(([dimension, item]) => (
            <div key={dimension} className="survey-profile-card">
              <h3>{DIMENSION_LABELS[dimension] || dimension}</h3>
              {item ? (
                <>
                  <div className="survey-text-row">
                    <span>Значение:</span>
                    <strong>{formatCategoryLabel(dimension, item.value)}</strong>
                  </div>
                  <div className="survey-text-row">
                    <span>Респондентов:</span>
                    <strong>{formatCount(item.respondents)}</strong>
                  </div>
                  <div className="survey-text-row">
                    <span>Выручка:</span>
                    <strong>{formatMoney(item.revenueRub)}</strong>
                  </div>
                </>
              ) : (
                <div className="empty-message">Анкеты отсутствуют</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SurveyMetricsTab;
